/* eslint-disable react/jsx-props-no-spreading */
import { useState } from 'react';
import { bool, func, node, string } from 'prop-types';
import { noop } from 'lodash';
import clsx from 'clsx';
import { IconButton, List, ListItem, ListItemIcon, ListItemText, Popover, Tooltip } from '@material-ui/core';

import useStyles from './ActionStyles';

const defaultPlacement = 'right';

export function Spacer() {
    const classes = useStyles();
    return <div className={classes.actionSpacer} />;
}

export function Action({ tooltip, tooltipPlacement = defaultPlacement, icon, disabled, onClick }) {
    function handleClick(event, callback) {
        event.preventDefault();
        event.stopPropagation();
        callback();
    }

    return (
        <Tooltip title={tooltip} placement={tooltipPlacement}>
            {/* The span is needed so that the tooltip shows even when the IconButton is disabled */}
            <span>
                <IconButton disabled={disabled} onClick={(event) => handleClick(event, onClick)}>
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );
}

Action.propTypes = {
    tooltip: string,
    icon: node.isRequired,
    disabled: bool,
    onClick: func,
};

Action.defaultProps = {
    tooltip: undefined,
    disabled: false,
    onClick: noop,
};

export function MenuAction({ tooltip, tooltipPlacement = defaultPlacement, icon, children }) {
    const classes = useStyles();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const anchorOrigin = {
        vertical: 'bottom',
        horizontal: 'right',
    };
    const transformOrigin = {
        vertical: 'top',
        horizontal: 'left',
    };

    function handleOpenMenu(event) {
        setMenuAnchor(event.currentTarget);
    }

    function handleCloseMenu() {
        setMenuAnchor(null);
    }

    return (
        <>
            <Tooltip title={tooltip} placement={tooltipPlacement}>
                {/* The span is needed so that the tooltip shows even when the IconButton is disabled */}
                <span>
                    <IconButton
                        className={clsx({ [classes.activeAction]: menuAnchor !== null })}
                        onClick={handleOpenMenu}
                    >
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>
            <Popover
                anchorEl={menuAnchor}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                open={menuAnchor !== null}
                onClose={handleCloseMenu}
            >
                <List className={classes.actionMenu} onClick={handleCloseMenu}>
                    {children}
                </List>
            </Popover>
        </>
    );
}

MenuAction.propTypes = {
    tooltip: string,
    icon: node.isRequired,
    children: node.isRequired,
};

MenuAction.defaultProps = {
    tooltip: undefined,
};

export function MenuActionItem({ icon, label, onClick }) {
    return (
        <ListItem onClick={onClick}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{label}</ListItemText>
        </ListItem>
    );
}

MenuActionItem.propTypes = {
    icon: node.isRequired,
    label: string.isRequired,
    onClick: func,
};

MenuActionItem.defaultProps = {
    onClick: noop,
};

export default function Actions({ children, className, compact = false }) {
    const classes = useStyles();
    return <span className={clsx(className, classes.actions, { compact })}>{children}</span>;
}

Actions.propTypes = {
    children: node.isRequired,
    className: string,
};

Actions.defaultProps = {
    className: undefined,
};
